import { createServerFn } from '@tanstack/react-start';
import { createClient } from '@supabase/supabase-js';

// Busca do site: sugestões em grupos (lojas, promoções, categorias, coleções, produtos, blog)
// e interpretação de frases ("vestido vermelho", "tênis masculino nike") em filtros.

function publicDb() {
  const key = process.env['SUPABASE_PUBLISHABLE_KEY'];
  const url = process.env['SUPABASE_URL'];
  if (!key || !url) return null;
  return createClient(url, key, {
    auth: { persistSession: false },
    global: { fetch: (input, init) => {
      const headers = new Headers(init?.headers);
      if (key.startsWith('sb_') && headers.get('Authorization') === `Bearer ${key}`) headers.delete('Authorization');
      headers.set('apikey', key);
      return fetch(input, { ...init, headers });
    } },
  }) as any;
}

export const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
const stem = (w: string) => w.replace(/(oes|aes)$/, 'ao').replace(/(is)$/, 'l').replace(/(es|s)$/, '');
const slugify = (s: string) => norm(s).replace(/ /g, '-');

const AUD: Record<string, string> = {
  masculino: 'masculino', masculina: 'masculino', masculinos: 'masculino', masculinas: 'masculino', homem: 'masculino', homens: 'masculino',
  feminino: 'feminino', feminina: 'feminino', femininos: 'feminino', femininas: 'feminino', mulher: 'feminino', mulheres: 'feminino',
  infantil: 'infantil', infantis: 'infantil', crianca: 'infantil', criancas: 'infantil', kids: 'infantil', juvenil: 'infantil',
  menino: 'menino', meninos: 'menino', menina: 'menina', meninas: 'menina', bebe: 'bebe', bebes: 'bebe', baby: 'bebe',
};
const COL: Record<string, string> = {};
for (const [c, ws] of Object.entries({
  vermelho: 'vermelho vermelha vermelhos vermelhas vinho bordo', preto: 'preto preta pretos pretas', branco: 'branco branca brancos brancas',
  azul: 'azul azuis marinho', verde: 'verde verdes', rosa: 'rosa pink', amarelo: 'amarelo amarela amarelos amarelas', laranja: 'laranja',
  roxo: 'roxo roxa lilas', cinza: 'cinza', marrom: 'marrom marrons caramelo', bege: 'bege nude', estampado: 'estampado estampada estampados estampadas floral',
})) for (const w of ws.split(' ')) COL[w] = c;
const STOP = new Set(['de', 'da', 'do', 'das', 'dos', 'para', 'pra', 'com', 'em', 'e', 'o', 'a', 'os', 'as', 'um', 'uma', 'cor', 'promocao', 'oferta', 'ofertas', 'barato', 'barata']);

export type ParsedQuery = { q: string; audience?: string; color?: string; loja?: string; categorySlug?: string; subSlug?: string; categoryName?: string; lojaName?: string };

async function parse(db: any, raw: string): Promise<ParsedQuery> {
  const words = norm(raw).split(' ').filter(Boolean).slice(0, 8);
  const out: ParsedQuery = { q: '' };
  const rest: string[] = [];
  for (const w of words) {
    if (!out.audience && AUD[w]) out.audience = AUD[w];
    else if (!out.color && COL[w]) out.color = COL[w];
    else if (!STOP.has(w)) rest.push(w);
  }
  // Loja: nome exato de uma palavra ou duas
  if (rest.length) {
    const cands = [...rest, ...rest.slice(0, -1).map((w, i) => `${w} ${rest[i + 1]}`)].filter(w => w.length >= 3);
    const { data: bs } = await db.from('brands').select('slug,name').eq('is_active', true).is('merged_into_id', null).in('slug', cands.map(slugify)).limit(1);
    if (bs?.[0]) { out.loja = bs[0].slug; out.lojaName = bs[0].name; const bw = norm(bs[0].name).split(' '); for (const w of bw) { const i = rest.indexOf(w); if (i >= 0) rest.splice(i, 1); } }
  }
  // Categoria: primeira palavra do nome da categoria (singular) igual a uma palavra digitada
  if (rest.length) {
    const { data: cats } = await db.from('product_categories').select('id,slug,name,parent_id');
    const all: any[] = cats ?? [];
    const stems = rest.map(stem);
    let hit: any = all.filter(c => c.parent_id).find(c => stems.includes(stem(norm(c.name).split(' ')[0])))
      ?? all.filter(c => !c.parent_id).find(c => stems.includes(stem(norm(c.name).split(' ')[0])));
    if (hit && out.audience && ['infantil', 'menino', 'menina', 'bebe'].includes(out.audience)) {
      // Produto infantil: usa a subcategoria da área Infantil
      const parentSlug = all.find(p => p.id === hit.parent_id)?.slug ?? hit.slug;
      const kid = hit.slug === 'pijamas' ? 'pijamas-infantis' : hit.slug === 'moda-praia' ? 'moda-praia-infantil'
        : parentSlug === 'calcados' ? 'calcados-infantis' : parentSlug === 'moda' ? 'roupas-infantis' : parentSlug === 'acessorios' ? 'acessorios-infantis' : null;
      const k = kid ? all.find(c => c.slug === kid) : null;
      if (k) { const i = stems.indexOf(stem(norm(hit.name).split(' ')[0])); if (i >= 0 && !/ e /.test(hit.name)) { /* palavra removida abaixo */ } hit = { ...k, _word: norm(hit.name).split(' ')[0] }; }
    }
    if (hit) {
      const top = hit.parent_id ? all.find(p => p.id === hit.parent_id) : hit;
      out.categorySlug = top.slug; if (hit.parent_id) out.subSlug = hit.slug; out.categoryName = hit.name;
      // Categorias compostas ("Vestidos e saias") mantêm a palavra para filtrar pelo título.
      const word = hit._word ?? norm(hit.name).split(' ')[0];
      if (hit._word || !/ e /.test(hit.name)) { const i = stems.indexOf(stem(word)); if (i >= 0) rest.splice(i, 1); }
    }
  }
  out.q = rest.join(' ');
  return out;
}

export type Suggestions = {
  stores: { slug: string; name: string; logo_url: string | null }[];
  categories: { label: string; url: string }[];
  products: { slug: string; title: string; image_url: string; price: number; store: string }[];
  posts: { slug: string; title: string }[];
  searchUrl: string;
};

// Índice leve de todas as lojas ativas: o buscador filtra no navegador (instantâneo).
export type StoreIndexItem = { slug: string; name: string; logo_url: string | null; f: boolean };
let storeIndexCache: { at: number; items: StoreIndexItem[] } | null = null;
export const listStoreIndex = createServerFn({ method: 'POST' }).handler(async (): Promise<StoreIndexItem[]> => {
  if (storeIndexCache && Date.now() - storeIndexCache.at < 120_000) return storeIndexCache.items;
  const db = publicDb();
  if (!db) return storeIndexCache?.items ?? [];
  const { data, error } = await db.from('brands').select('slug,name,logo_url,is_featured').eq('is_active', true).is('merged_into_id', null)
    .not('slug', 'is', null).limit(3000).abortSignal(AbortSignal.timeout(4000));
  if (error || !data?.length) return storeIndexCache?.items ?? [];
  const items = data.map((b: any) => ({ slug: b.slug, name: b.name, logo_url: b.logo_url ?? null, f: !!b.is_featured }));
  storeIndexCache = { at: Date.now(), items };
  return items;
});

// Reserva: só lojas (cupons/promoções), usada enquanto o índice local não chegou.
export const suggestStores = createServerFn({ method: 'POST' })
  .inputValidator((d: { q: string; direct?: boolean }) => ({ q: String(d?.q ?? '').slice(0, 80), direct: !!d?.direct }))
  .handler(async ({ data }): Promise<Pick<Suggestions, 'stores'>> => {
    const q = norm(data.q);
    if (q.length < 2) return { stores: [] };
    const { searchSite } = await import('./typesense.server');
    // direct: a lista guardada não achou a loja (pode ser nova) → vai direto ao banco.
    const hit = data.direct ? null : await searchSite(data.q, 600);
    if (hit) return { stores: hit.stores.map(s => ({ slug: s.slug ?? '', name: s.title, logo_url: s.img ?? null })).filter(s => s.slug) };
    const db = publicDb();
    if (!db) return { stores: [] };
    const { data: s } = await db.from('brands').select('slug,name,logo_url').eq('is_active', true).is('merged_into_id', null)
      .ilike('name', `%${q.replace(/ /g, '%')}%`).order('is_featured', { ascending: false }).limit(5).abortSignal(AbortSignal.timeout(1500));
    return { stores: (s ?? []).filter((b: any) => b.slug) };
  });

export const suggest = createServerFn({ method: 'POST' })
  .inputValidator((d: { q: string; skipStores?: boolean }) => ({ q: String(d?.q ?? '').slice(0, 80), skipStores: !!d?.skipStores }))
  .handler(async ({ data }): Promise<Suggestions> => {
    const q = norm(data.q);
    const empty: Suggestions = { stores: [], categories: [], products: [], posts: [], searchUrl: `/ofertas/busca?q=${encodeURIComponent(data.q)}` };
    const db = publicDb();
    if (!db || q.length < 2) return empty;
    const like = `%${q.replace(/ /g, '%')}%`;
    const words = q.split(' ').filter(w => w.length >= 2 && !STOP.has(w)).slice(0, 4);
    let prod = db.from('deal_products').select('slug,title,image_url,price,brand:brands!inner(name)').eq('available', true).is('parent_deal_id', null)
      .gte('checked_at', new Date(Date.now() - 10 * 86400_000).toISOString()).order('discount_percent', { ascending: false }).limit(5)
      .abortSignal(AbortSignal.timeout(3000));
    for (const w of words) prod = prod.ilike('title', `%${w}%`);
    // Typesense primeiro (tolera erro de digitação); o banco é a reserva.
    const tsProd = (async () => {
      const { searchDealIds } = await import('./typesense.server');
      const hit = await searchDealIds({
        q: data.q, query_by: 'title,brand_name', filter_by: `available:=true && checked_at:>=${Math.floor(Date.now() / 1000) - 10 * 86400}`,
        sort_by: '_text_match:desc,discount_percent:desc', per_page: 5, num_typos: 2, prefix: 'true', include_fields: 'id',
      }, 1200);
      if (!hit) return null;
      if (!hit.ids.length) return { data: [] };
      const { data: rows } = await db.from('deal_products').select('id,slug,title,image_url,price,brand:brands!inner(name)').in('id', hit.ids);
      const m = new Map((rows ?? []).map((r: any) => [r.id, r]));
      return { data: hit.ids.map(id => m.get(id)).filter(Boolean) };
    })().catch(() => null);
    const safeProd = tsProd.then(r => r ?? Promise.resolve(prod).then((x: any) => x, () => ({ data: [] })));
    // Lojas, categorias, coleções e blog numa só pergunta ao Typesense; banco só se ele falhar.
    const { searchSite } = await import('./typesense.server');
    const site = await searchSite(data.q, 1200);
    const none = Promise.resolve({ data: [] as any[] });
    const [stores, cols, cats, prods, posts, parsed] = await Promise.all([
      data.skipStores || site ? none : db.from('brands').select('slug,name,logo_url').eq('is_active', true).is('merged_into_id', null).ilike('name', like).order('is_featured', { ascending: false }).limit(5),
      site ? none : db.from('product_collections').select('slug,title').eq('status', 'active').ilike('title', like).order('product_count', { ascending: false }).limit(4),
      site ? none : db.from('product_categories').select('slug,name,parent_id').ilike('name', `%${words[0] ?? q}%`).limit(4),
      words.length ? safeProd : Promise.resolve({ data: [] }),
      site ? none : db.from('blog_posts').select('slug,title').eq('status', 'published').ilike('title', like).order('published_at', { ascending: false }).limit(3),
      parse(db, data.q),
    ]);
    const { data: allCats } = cats.data?.some((c: any) => c.parent_id) ? await db.from('product_categories').select('id,slug').is('parent_id', null) : { data: [] };
    const categories: Suggestions['categories'] = [];
    for (const c of cols.data ?? []) categories.push({ label: c.title, url: `/ofertas/${c.slug}` });
    if (parsed.categorySlug && (parsed.audience || parsed.color)) {
      const aud = parsed.audience ? ` ${parsed.audience === 'bebe' ? 'bebê' : parsed.audience}` : '';
      categories.push({ label: `${parsed.categoryName}${aud}${parsed.color ? ` ${parsed.color}` : ''}`, url: `/ofertas/busca?q=${encodeURIComponent(data.q)}` });
    }
    for (const c of site?.categories ?? []) categories.push({ label: c.sub ? `${c.title} · ${c.sub}` : c.title, url: c.url });
    for (const c of cats.data ?? []) {
      const top = c.parent_id ? (allCats ?? []).find((t: any) => t.id === c.parent_id) : null;
      categories.push({ label: c.name, url: top ? `/ofertas/${top.slug}/${c.slug}` : `/ofertas/${c.slug}` });
    }
    const siteStores = data.skipStores ? [] : (site?.stores ?? []).map(s => ({ slug: s.slug ?? '', name: s.title, logo_url: s.img ?? null }));
    return {
      stores: [...siteStores, ...(stores.data ?? [])].filter((b: any) => b.slug),
      categories: categories.slice(0, 6),
      products: (prods.data ?? []).map((p: any) => ({ slug: p.slug, title: p.title, image_url: p.image_url, price: Number(p.price), store: p.brand?.name ?? '' })),
      posts: site ? site.posts.map(p => ({ slug: p.url.replace(/^\//, ''), title: p.title })) : (posts.data ?? []),
      searchUrl: empty.searchUrl,
    };
  });

// Página de resultados: interpreta a frase e, se houver coleção igual, indica o endereço dela.
export const parseSearch = createServerFn({ method: 'POST' })
  .inputValidator((d: { q: string }) => ({ q: String(d?.q ?? '').slice(0, 80) }))
  .handler(async ({ data }): Promise<ParsedQuery & { collection: string | null; path: string | null }> => {
    const key = norm(data.q);
    const hit = parseCache.get(key);
    if (hit && Date.now() - hit.at < 15 * 60_000) return hit.res;
    const fallback = { q: data.q, collection: null, path: null } as ParsedQuery & { collection: string | null; path: string | null };
    const res = await Promise.race([
      parseSearchImpl(data.q).catch(() => fallback),
      new Promise<typeof fallback>(r => setTimeout(() => r(fallback), 800)),
    ]);
    if (res !== fallback) { parseCache.set(key, { at: Date.now(), res }); if (parseCache.size > 1000) parseCache.delete(parseCache.keys().next().value!); }
    return res;
  });

const parseCache = new Map<string, { at: number; res: ParsedQuery & { collection: string | null; path: string | null } }>();
async function parseSearchImpl(q: string): Promise<ParsedQuery & { collection: string | null; path: string | null }> {
  const data = { q };
  {
    const db = publicDb();
    if (!db || norm(data.q).length < 2) return { q: '', collection: null, path: null };
    const slug = slugify(data.q);
    const [{ data: col }, parsed] = await Promise.all([
      db.from('product_collections').select('slug').eq('status', 'active').eq('slug', slug).maybeSingle(),
      parse(db, data.q),
    ]);
    let hit = col?.slug ?? null;
    // Frase = categoria + cor/público de uma coleção pronta (ex.: "vestido vermelho" → vestidos-vermelhos)
    if (!hit && parsed.categorySlug && (parsed.color || parsed.audience) && !parsed.loja) {
      const { data: cat } = await db.from('product_categories').select('id').eq('slug', parsed.subSlug || parsed.categorySlug).maybeSingle();
      if (cat) {
        let q = db.from('product_collections').select('slug,terms').eq('status', 'active').eq('indexable', true).eq('category_id', cat.id);
        q = parsed.color ? q.eq('color', parsed.color) : q.is('color', null);
        q = parsed.audience ? q.eq('audience', parsed.audience) : q.is('audience', null);
        const { data: cs } = await q.limit(5);
        const rest = norm(parsed.q).split(' ').filter(Boolean).map(stem);
        const m = (cs ?? []).find((c: any) => rest.every(w => (c.terms ?? []).some((t: string) => stem(norm(t)) === w)));
        hit = m?.slug ?? null;
      }
    }
    // Categoria + público sem outros filtros → página própria (ex.: /ofertas/moda/camisetas/masculino)
    let path: string | null = null;
    if (!hit && parsed.categorySlug && !parsed.color && !parsed.loja && (!parsed.q || stem(norm(parsed.q)) === stem(norm(parsed.categoryName ?? '').split(' ')[0]))) {
      const kidTop = parsed.categorySlug === 'infantil';
      const aud = parsed.audience && (kidTop ? ['menino', 'menina'].includes(parsed.audience) : ['masculino', 'feminino'].includes(parsed.audience)) ? parsed.audience : null;
      if (aud || !parsed.audience || (kidTop && parsed.audience === 'infantil')) path = `/ofertas/${parsed.categorySlug}${parsed.subSlug ? `/${parsed.subSlug}` : ''}${aud ? `/${aud}` : ''}`;
    }
    return { ...parsed, collection: hit, path };
  }
}

// Registra o termo (alimenta sugestões de coleções no painel).
export const logSearch = createServerFn({ method: 'POST' })
  .inputValidator((d: { q: string; results: number }) => ({ q: String(d?.q ?? '').slice(0, 60), results: Math.max(0, Math.floor(Number(d?.results) || 0)) }))
  .handler(async ({ data }) => {
    if (norm(data.q).length < 3) return { ok: false };
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server');
    await (supabaseAdmin as any).rpc('log_search', { _term: data.q, _results: data.results });
    return { ok: true };
  });
